import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import styles from './CompositionRowField.module.css';
import ThemeContext from 'config/context/themeContext';
import dataTypesConfig from '../../constants/dataTypes.json';
import { typeConverters, converterFor } from '../../utils/typeConverters';
import { valiedateNameField } from 'utils/validations';

const createDefaultValue = (field) => {
    if (!field) return '';
    if (field.is_object && field.object?.fields) {
        return field.is_array ? [] : createDefaultObject(field.object.fields);
    }
    if (field.is_array) return [];
    switch (field.type) {
        case dataTypesConfig.supportedTypes.BOOL:
            return false;
        case dataTypesConfig.supportedTypes.UINT128_T:
        case dataTypesConfig.supportedTypes.UINT64_T:
        case dataTypesConfig.supportedTypes.UINT32_T:
        case dataTypesConfig.supportedTypes.UINT16_T:
        case dataTypesConfig.supportedTypes.UINT8_T:
        case dataTypesConfig.supportedTypes.INT64_T:
        case dataTypesConfig.supportedTypes.INT32_T:
        case dataTypesConfig.supportedTypes.INT16_T:
        case dataTypesConfig.supportedTypes.INT8_T:
        case dataTypesConfig.supportedTypes.DOUBLE:
        case dataTypesConfig.supportedTypes.FLOAT:
            return '';
        default:
            return '';
    }
};

const createDefaultObject = (fields = []) => {
    const out = {};
    fields.forEach((f) => {
        out[f.name] = createDefaultValue(f);
    });
    return out;
};

const getTypeHint = (field) => {
    if (!field) return 'value';
    if (field.is_array && field.is_object && field.object?.name) return `[Array<${field.object.name}>]`;
    if (field.is_array && field.is_object) return '[Array<Object>]';
    if (field.is_object && field.object?.name) return `[${field.object.name}]`;
    if (field.is_object) return '[Object]';
    if (field.is_array) return `[Array<${field.type || 'value'}>]`;
    return `[${field.type || 'value'}]`;
};

const getArrayItemTypeHint = (field) => {
    if (!field) return '[value]';
    if (field.is_object && field.object?.name) return `[${field.object.name}]`;
    if (field.is_object) return '[Object]';
    return `[${field.type || 'value'}]`;
};

const StructuredFieldEditor = ({ field, value, onValueChange, isEditable, depth = 0 }) => {
    if (!field) return null;

    if (field.is_object && field.object?.fields) {
        if (field.is_array) {
            const items = Array.isArray(value) ? value : [];
            return (
                <div className={styles.object_group}>
                    {items.map((item, idx) => (
                        <div key={idx} className={styles.object_card}>
                            <div className={styles.object_card_header}>
                                <strong className={styles.object_name}>Item {idx + 1} {getTypeHint(field)}</strong>
                                {isEditable && (
                                    <button
                                        type="button"
                                        className={`${styles.small_btn} ${styles.small_btn_danger}`}
                                        onClick={() => onValueChange(items.filter((_, i) => i !== idx))}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                            {field.object.fields.map((nested) => (
                                <div
                                    key={nested.meta_name || nested.name}
                                    className={styles.object_row}
                                    style={{ paddingLeft: `${(depth + 1) * 12}px` }}
                                >
                                    <label className={styles.object_name}>
                                        {nested.name} {getTypeHint(nested)}
                                    </label>
                                    <StructuredFieldEditor
                                        field={nested}
                                        value={(item || {})[nested.name]}
                                        isEditable={isEditable}
                                        depth={depth + 1}
                                        onValueChange={(next) => {
                                            const updated = [...items];
                                            updated[idx] = { ...(updated[idx] || {}), [nested.name]: next };
                                            onValueChange(updated);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    ))}
                    {isEditable && (
                        <button
                            type="button"
                            className={styles.small_btn}
                            onClick={() => onValueChange([...(items || []), createDefaultObject(field.object.fields)])}
                        >
                            Add item
                        </button>
                    )}
                </div>
            );
        }

        const current = (value && typeof value === 'object' && !Array.isArray(value)) ? value : createDefaultObject(field.object.fields);
        return (
            <div className={styles.object_group}>
                {field.object.fields.map((nested) => (
                    <div
                        key={nested.meta_name || nested.name}
                        className={styles.object_row}
                        style={{ paddingLeft: `${(depth + 1) * 12}px` }}
                    >
                        <label className={styles.object_name}>
                            {nested.name} {getTypeHint(nested)}
                        </label>
                        <StructuredFieldEditor
                            field={nested}
                            value={current[nested.name]}
                            isEditable={isEditable}
                            depth={depth + 1}
                            onValueChange={(next) => onValueChange({ ...current, [nested.name]: next })}
                        />
                    </div>
                ))}
            </div>
        );
    }

    if (field.is_array) {
        const items = Array.isArray(value) ? value : [];
        return (
            <div className={styles.object_group}>
                {items.map((item, idx) => (
                    <div key={idx} className={styles.array_row} style={{ paddingLeft: `${(depth + 1) * 12}px` }}>
                        <input
                            className={styles.structured_input}
                            type={field.type === dataTypesConfig.supportedTypes.BOOL ? 'text' : (field.type === dataTypesConfig.supportedTypes.DOUBLE || field.type === dataTypesConfig.supportedTypes.FLOAT || field.type?.includes('int') || field.type?.includes('uint') ? 'number' : 'text')}
                            value={item ?? ''}
                            readOnly={!isEditable}
                            placeholder={getArrayItemTypeHint(field)}
                            onChange={(e) => {
                                const updated = [...items];
                                updated[idx] = e.target.value;
                                onValueChange(updated);
                            }}
                        />
                        {isEditable && (
                            <button type="button" className={`${styles.small_btn} ${styles.small_btn_danger}`} onClick={() => onValueChange(items.filter((_, i) => i !== idx))}>
                                Remove
                            </button>
                        )}
                    </div>
                ))}
                {isEditable && (
                    <button type="button" className={styles.small_btn} onClick={() => onValueChange([...(items || []), ''])}>
                        Add item
                    </button>
                )}
            </div>
        );
    }

    if (field.type === dataTypesConfig.supportedTypes.BOOL) {
        return (
            <div className={styles.bool_inline}>
                <label>
                    <input
                        className={styles.structured_checkbox}
                        type="checkbox"
                        checked={!!value}
                        disabled={!isEditable}
                        onChange={(e) => onValueChange(e.target.checked)}
                    />
                    <span>{value ? 'true' : 'false'}</span>
                </label>
            </div>
        );
    }

    const numericType = field.type === dataTypesConfig.supportedTypes.DOUBLE
        || field.type === dataTypesConfig.supportedTypes.FLOAT
        || field.type?.includes('int')
        || field.type?.includes('uint');

    return (
        <input
            className={styles.structured_input}
            type={numericType ? 'number' : 'text'}
            value={value ?? ''}
            readOnly={!isEditable}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={getTypeHint(field)}
        />
    );
};

const CompositionRowField = ({ type, value, onChange, isEditable = false, isObject = false, isArray = false, fieldDefinition = null }) => {
    const { dark } = useContext(ThemeContext);
    const [localValue, setLocalValue] = useState('');
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const arrayPlaceholder = useMemo(() => {
        switch (type) {
            case dataTypesConfig.supportedTypes.STRING:
            case dataTypesConfig.supportedTypes.NAME:
                return '["text", "text2"]';
            case dataTypesConfig.supportedTypes.BOOL:
                return '[true, false]';
            case dataTypesConfig.supportedTypes.TIME_POINT:
                return '["2024-01-01T00:00:00Z", "2024-06-01T12:30:00Z"]';
            case dataTypesConfig.supportedTypes.UINT128_T:
            case dataTypesConfig.supportedTypes.UINT64_T:
            case dataTypesConfig.supportedTypes.UINT32_T:
            case dataTypesConfig.supportedTypes.UINT16_T:
            case dataTypesConfig.supportedTypes.UINT8_T:
            case dataTypesConfig.supportedTypes.INT64_T:
            case dataTypesConfig.supportedTypes.INT32_T:
            case dataTypesConfig.supportedTypes.INT16_T:
            case dataTypesConfig.supportedTypes.INT8_T:
                return '[1, 2, 3]';
            case dataTypesConfig.supportedTypes.DOUBLE:
            case dataTypesConfig.supportedTypes.FLOAT:
                return '[1.23, 4.56]';
            default:
                return '["value1", "value2"]';
        }
    }, [type]);

    // Konvertor po POLJU, ne samo po tipu: tačne decimale se na lancu čuvaju
    // kao ceo broj, a broj decimala stoji uz kolonu (`scale`).
    const converter = useMemo(
        () => converterFor(type, fieldDefinition?.scale),
        [type, fieldDefinition?.scale],
    );

    // Sync with external value changes
    useEffect(() => {
        if (value === undefined) return;
        if (isObject || isArray) {
            try {
                const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                setLocalValue(text);
            } catch (_) {
                setLocalValue('');
            }
        } else {
            setLocalValue(converter.toDisplay(value));
        }
    }, [value, converter, isObject, isArray]);

    const handleChange = (newValue) => {
        if (!onChange || !isEditable) return;
        if (type === dataTypesConfig.supportedTypes.NAME) {
            // Allow empty to propagate, validate non-empty values
            if (newValue === '' || valiedateNameField(newValue)) {
                setLocalValue(newValue);
                onChange(converter.fromDisplay(newValue));
            }
            return;
        }
        setLocalValue(newValue);
        onChange(converter.fromDisplay(newValue));
    };

    // Render different input types
    const renderInput = () => {
        if (isObject) {
            return (
                <>
                    <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)} disabled={!isEditable}>
                        {localValue ? 'Edit object' : 'Add object'}
                    </button>
                    {open && (
                        <div className={styles.json_popover} ref={popoverRef}>
                            <StructuredFieldEditor
                                field={fieldDefinition || { type, is_object: isObject, is_array: isArray }}
                                value={(() => {
                                    try {
                                        return typeof localValue === 'string' && localValue ? JSON.parse(localValue) : (value || createDefaultObject(fieldDefinition?.object?.fields || []));
                                    } catch (_) {
                                        return value || createDefaultObject(fieldDefinition?.object?.fields || []);
                                    }
                                })()}
                                isEditable={isEditable}
                                onValueChange={(next) => {
                                    try {
                                        setLocalValue(JSON.stringify(next, null, 2));
                                    } catch (_) {
                                        setLocalValue('');
                                    }
                                }}
                            />
                            <div className={styles.json_actions}>
                                <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Cancel</button>
                                <button className={styles.primary} type="button" onClick={() => {
                                    try {
                                        const parsed = localValue ? JSON.parse(localValue) : createDefaultObject(fieldDefinition?.object?.fields || []);
                                        if (onChange && isEditable) onChange(parsed);
                                        setOpen(false);
                                    } catch (_) {
                                        // keep popover open for correction
                                    }
                                }}>Save</button>
                            </div>
                        </div>
                    )}
                </>
            );
        }
        if (isArray) {
            return (
                <>
                    <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)} disabled={!isEditable}>
                        {localValue ? 'Edit list' : 'Add list'}
                    </button>
                    {open && (
                        <div className={styles.json_popover} ref={popoverRef}>
                            <textarea 
                                className={styles.json_textarea}
                                value={localValue}
                                onChange={(e) => setLocalValue(e.target.value)}
                                readOnly={!isEditable}
                                placeholder={arrayPlaceholder}
                            />
                            <div className={styles.json_actions}>
                                <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Cancel</button>
                                <button className={styles.primary} type="button" onClick={() => {
                                    try {
                                        let text = (localValue || '').trim();
                                        // If user typed comma-separated without brackets, wrap it
                                        const looksLikeCSV = text && !text.startsWith('[') && text.includes(',');
                                        if (looksLikeCSV) {
                                            text = `[${text}]`;
                                        }
                                        // Lenient: single quotes to double
                                        text = text.replace(/'([^']*)'/g, '"$1"');
                                        // Remove trailing commas
                                        text = text.replace(/,\s*([}\]])/g, '$1');
                                        const parsed = text ? JSON.parse(text) : [];
                                        if (Array.isArray(parsed)) {
                                            if (onChange && isEditable) onChange(parsed);
                                            setLocalValue(text);
                                            setOpen(false);
                                        }
                                    } catch (_) {
                                        // keep open for correction
                                    }
                                }}>Save</button>
                            </div>
                        </div>
                    )}
                </>
            );
        }
        switch (type) {
            case dataTypesConfig.supportedTypes.STRING:
            case dataTypesConfig.supportedTypes.NAME:
                return (
                    <input 
                        type="text" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );

            case dataTypesConfig.supportedTypes.UINT128_T:
            case dataTypesConfig.supportedTypes.UINT64_T:
            case dataTypesConfig.supportedTypes.UINT32_T:
            case dataTypesConfig.supportedTypes.UINT16_T:
            case dataTypesConfig.supportedTypes.UINT8_T:
            case dataTypesConfig.supportedTypes.INT64_T:
            case dataTypesConfig.supportedTypes.INT32_T:
            case dataTypesConfig.supportedTypes.INT16_T:
            case dataTypesConfig.supportedTypes.INT8_T:
            case dataTypesConfig.supportedTypes.DOUBLE:
            case dataTypesConfig.supportedTypes.FLOAT:
                return (
                    <input 
                        type="number" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                        step={type === dataTypesConfig.supportedTypes.DOUBLE || type === dataTypesConfig.supportedTypes.FLOAT ? "any" : "1"}
                    />
                );

            case dataTypesConfig.supportedTypes.TIME_POINT:
                return (
                    <input 
                        type="datetime-local" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );

            case dataTypesConfig.supportedTypes.BOOL:
                return (
                    <div className={styles.radio_thumb}>
                        <input
                            type="checkbox"
                            checked={localValue === 'true'}
                            onChange={(e) => handleChange(e.target.checked.toString())}
                            disabled={!isEditable}
                        />
                    </div>
                );

            default:
                return (
                    <input 
                        type="text" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );
        }
    };

    if (isObject) {
        return (
            <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
                <div className={styles.field_holder}>
                    {renderInput()}
                </div>
            </div>
        );
    }

    if (!typeConverters[type]) {
        return (
            <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
                <div className={styles.field_holder}>
                    <span>Unsupported type: {type}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
            <div className={styles.field_holder}>
                {renderInput()}
            </div>
        </div>
    );
}

export default CompositionRowField;